import type { ReportScheduleUnit } from './api';
import type { ReportScheduleOptions } from './report-schedule-options';

/** O horário de um relatório como a API o grava: a frase que a tela escreve e as próximas datas. */
export class ReportSchedule {
  static readonly units: { value: ReportScheduleUnit; one: string; many: string; every: string }[] = [
    { value: 'hour', one: 'Hora', many: 'Horas', every: 'Toda hora' },
    { value: 'day', one: 'Dia', many: 'Dias', every: 'Todo dia' },
    { value: 'week', one: 'Semana', many: 'Semanas', every: 'Toda semana' },
    { value: 'month', one: 'Mês', many: 'Meses', every: 'Todo mês' },
  ];

  /** Segunda primeiro, como se lê a semana no Brasil e como a API numera. */
  static readonly weekdays: { day: number; short: string; name: string }[] = [
    { day: 1, short: 'Seg', name: 'segunda' },
    { day: 2, short: 'Ter', name: 'terça' },
    { day: 3, short: 'Qua', name: 'quarta' },
    { day: 4, short: 'Qui', name: 'quinta' },
    { day: 5, short: 'Sex', name: 'sexta' },
    { day: 6, short: 'Sáb', name: 'sábado' },
    { day: 7, short: 'Dom', name: 'domingo' },
  ];

  /** Até 28: todo mês tem esse dia, e o envio nunca pula fevereiro nem o mês de 30. */
  static readonly monthDays: number[] = Array.from({ length: 28 }, (_, index) => index + 1);

  private static readonly searchLimit = 400;

  constructor(private readonly options: ReportScheduleOptions) {}

  static unitLabel(unit: (typeof ReportSchedule.units)[number], interval: number): string {
    return interval === 1 ? unit.one : unit.many;
  }

  private get interval(): number {
    return Math.max(1, Math.floor(this.options.interval || 1));
  }

  private get unit(): (typeof ReportSchedule.units)[number] {
    return ReportSchedule.units.find((unit) => unit.value === this.options.unit) ?? ReportSchedule.units[0];
  }

  describe(): string {
    const every = this.interval === 1 ? this.unit.every : `A cada ${this.interval} ${this.unit.many.toLowerCase()}`;
    if (this.isMissingDays()) return `${every}, sem dia escolhido`;
    const time = this.spokenTime();
    const sentences: Record<ReportScheduleUnit, () => string> = {
      hour: () => `${every}, a partir das ${time}`,
      day: () => `${every} às ${time}`,
      week: () => `${every}, ${this.listed(this.weekdayNames())} às ${time}`,
      month: () => `${every}, ${this.options.days.length > 1 ? 'dias' : 'dia'} ${this.listed(this.sortedDays().map(String))} às ${time}`,
    };
    return sentences[this.options.unit]();
  }

  /** Falta o dia que a unidade pede: sem ele não há próximo envio. */
  isMissingDays(): boolean {
    const needsDays: Record<ReportScheduleUnit, boolean> = {
      hour: false,
      day: false,
      week: this.options.days.length === 0,
      month: this.options.days.length === 0,
    };
    return needsDays[this.options.unit];
  }

  /** As próximas datas da regra a partir do início ou de `from`, o que vier depois. */
  nextSends({ count, from }: { count: number; from: Date }): string[] {
    return this.upcoming(from, count).map((date) => this.formatted(date));
  }

  /** O primeiro envio a partir de `from`, com a data para comparar e o texto para mostrar. */
  nextSend(from: Date): { at: Date; label: string } | null {
    const [at] = this.upcoming(from, 1);
    if (!at) return null;
    return { at, label: this.formatted(at) };
  }

  private upcoming(from: Date, count: number): Date[] {
    if (this.isMissingDays()) return [];
    const sends: Record<ReportScheduleUnit, () => Date[]> = {
      hour: () => this.stepping(from, count, (date, step) => date.setHours(date.getHours() + step)),
      day: () => this.stepping(from, count, (date, step) => date.setDate(date.getDate() + step)),
      week: () => this.weekly(from, count),
      month: () => this.monthly(from, count),
    };
    return sends[this.options.unit]().slice(0, count);
  }

  private start(): Date {
    const [year, month, day] = this.options.startsOn.split('-').map(Number);
    const [hour, minute] = this.options.sendTime.split(':').map(Number);
    return new Date(year ?? 2026, (month ?? 1) - 1, day ?? 1, hour ?? 0, minute ?? 0);
  }

  private stepping(from: Date, count: number, advance: (date: Date, step: number) => void): Date[] {
    const found: Date[] = [];
    for (let step = 0; found.length < count && step < ReportSchedule.searchLimit * 31; step += this.interval) {
      const date = this.start();
      advance(date, step);
      if (date >= from) found.push(date);
    }
    return found;
  }

  private weekly(from: Date, count: number): Date[] {
    const start = this.start();
    const monday = new Date(start);
    monday.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const found: Date[] = [];
    for (let week = 0; found.length < count && week < ReportSchedule.searchLimit; week += this.interval) {
      for (const day of this.sortedDays()) {
        const date = new Date(monday);
        date.setDate(monday.getDate() + week * 7 + day - 1);
        if (date >= start && date >= from && found.length < count) found.push(date);
      }
    }
    return found;
  }

  private monthly(from: Date, count: number): Date[] {
    const start = this.start();
    const found: Date[] = [];
    for (let month = 0; found.length < count && month < ReportSchedule.searchLimit; month += this.interval) {
      const lastDay = new Date(start.getFullYear(), start.getMonth() + month + 1, 0).getDate();
      for (const day of this.sortedDays().filter((monthDay) => monthDay <= lastDay)) {
        const date = new Date(start.getFullYear(), start.getMonth() + month, day, start.getHours(), start.getMinutes());
        if (date >= start && date >= from && found.length < count) found.push(date);
      }
    }
    return found;
  }

  private sortedDays(): number[] {
    return [...this.options.days].sort((first, second) => first - second);
  }

  private weekdayNames(): string[] {
    return ReportSchedule.weekdays.filter((weekday) => this.options.days.includes(weekday.day)).map((weekday) => weekday.name);
  }

  private listed(items: string[]): string {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} e ${items.at(-1)}`;
  }

  private spokenTime(): string {
    const [hour, minute] = this.options.sendTime.split(':');
    return `${Number(hour)}h${minute === '00' ? '' : minute}`;
  }

  private formatted(date: Date): string {
    const day = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (this.options.unit !== 'hour') return day;
    return `${day} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
}
